import React from 'react';
import { Calculator, Clock, TrendingUp, Zap, DollarSign, Layers, Scale, PieChart } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости 3D-печати
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Получаем символ валюты для отображения
  let currencySymbol = '₽';
  switch(results.currency) {
    case 'USD':
      currencySymbol = '$';
      break;
    case 'EUR':
      currencySymbol = '€';
      break;
    case 'CNY':
      currencySymbol = '¥';
      break;
    default:
      currencySymbol = '₽';
  }

  // Форматирование валюты с выбранным символом
  const formatCurrencyWithSymbol = (value) => {
    return `${formatNumber(value, 2)} ${currencySymbol}`;
  };

  // Форматирование времени
  const formatTime = (hours) => {
    if (hours < 0.017) { // меньше 1 минуты
      return "< 1 мин";
    } else if (hours < 1) {
      const minutes = Math.round(hours * 60);
      return `${minutes} мин`;
    } else if (hours < 24) {
      const h = Math.floor(hours);
      const mins = Math.round((hours - h) * 60);
      return mins > 0 ? `${h} ч ${mins} мин` : `${h} ч`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = Math.floor(hours % 24);
      return remainingHours > 0 ? `${days} д ${remainingHours} ч` : `${days} д`;
    }
  };

  // Находим название выбранного материала
  const getMaterialName = (materialType) => {
    const materialNames = {
      'pla': 'PLA',
      'abs': 'ABS',
      'petg': 'PETG',
      'nylon': 'Нейлон',
      'tpu': 'TPU (Гибкий)',
      'resin': 'Фотополимер',
      'nylon_powder': 'Нейлоновый порошок',
      'metal_powder': 'Металлический порошок'
    };
    
    return materialNames[materialType] || materialType;
  };

  // Находим название выбранного принтера
  const getPrinterName = (printerType) => {
    const printerNames = {
      'fdm': 'FDM (Послойное наплавление)',
      'sla': 'SLA (Стереолитография)',
      'sls': 'SLS (Селективное лазерное спекание)',
      'slm': 'SLM (Селективное лазерное плавление)',
      'polyjet': 'PolyJet (Струйная печать)'
    };
    
    return printerNames[printerType] || printerType;
  };

  return (
    <ToolResult title="Результаты расчета стоимости">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Стоимость за единицу */}
        <div className="rounded-lg bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30 p-5 border border-teal-100 dark:border-teal-800/50 shadow-sm">
          <h3 className="text-base font-medium text-teal-700 dark:text-teal-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            {results.includeProfit ? 'Продажная цена' : 'Себестоимость'} (за 1 шт.)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-teal-800 dark:text-teal-200" aria-live="polite">
              {formatCurrencyWithSymbol(results.sellingPriceUnit)}
            </span>
          </div>
          <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
            {results.includeProfit 
              ? `Включая ${formatNumber(results.profitAmount / results.totalCostPrice * 100, 1)}% прибыли` 
              : 'Без учета прибыли'}
          </p>
        </div>

        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Общая стоимость ({results.printQuantity} шт.)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrencyWithSymbol(results.sellingPriceTotal)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Себестоимость: {formatCurrencyWithSymbol(results.totalCostPrice)}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Технология печати" 
          value={getPrinterName(results.printerType)} 
          color="teal" 
          icon={<Calculator className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Материал" 
          value={getMaterialName(results.materialType)} 
          color="cyan" 
          icon={<Layers className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Расход материала на 1 изделие" 
          value={`${formatNumber(results.materialWeight, 2)} г (${formatNumber(results.totalMaterialVolume, 2)} см³)`} 
          icon={<Scale className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Время печати (1 шт.)" 
          value={formatTime(results.singlePrintTime)} 
          color="amber"
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Общее время печати" 
          value={formatTime(results.totalPrintTime)} 
          color="amber"
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Расход электроэнергии" 
          value={`${formatNumber(results.electricityConsumption, 2)} кВт⋅ч`} 
          color="indigo"
          icon={<Zap className="w-4 h-4"/>} 
        />
        
        <div className="mt-5">
          <div className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <PieChart className="w-5 h-5 mr-2 text-indigo-500" />
            Состав затрат
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {results.materialCost > 0 && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-100 dark:border-blue-800">
                <div className="font-medium text-blue-700 dark:text-blue-300 mb-1">Материал</div>
                <div className="text-xl font-bold text-blue-800 dark:text-blue-200">{formatCurrencyWithSymbol(results.materialCost)}</div>
                <div className="text-xs text-blue-600 dark:text-blue-400">{formatNumber(results.materialCost / results.totalCostPrice * 100, 1)}% от себестоимости</div>
              </div>
            )}
            
            {results.electricityCost > 0 && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-100 dark:border-yellow-800">
                <div className="font-medium text-yellow-700 dark:text-yellow-300 mb-1">Электроэнергия</div>
                <div className="text-xl font-bold text-yellow-800 dark:text-yellow-200">{formatCurrencyWithSymbol(results.electricityCost)}</div>
                <div className="text-xs text-yellow-600 dark:text-yellow-400">{formatNumber(results.electricityCost / results.totalCostPrice * 100, 1)}% от себестоимости</div>
              </div>
            )}
            
            {results.depreciationCost > 0 && (
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-md border border-purple-100 dark:border-purple-800">
                <div className="font-medium text-purple-700 dark:text-purple-300 mb-1">Амортизация</div>
                <div className="text-xl font-bold text-purple-800 dark:text-purple-200">{formatCurrencyWithSymbol(results.depreciationCost)}</div>
                <div className="text-xs text-purple-600 dark:text-purple-400">{formatNumber(results.depreciationCost / results.totalCostPrice * 100, 1)}% от себестоимости</div>
              </div>
            )}
            
            {results.laborCost > 0 && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-100 dark:border-green-800">
                <div className="font-medium text-green-700 dark:text-green-300 mb-1">Труд</div>
                <div className="text-xl font-bold text-green-800 dark:text-green-200">{formatCurrencyWithSymbol(results.laborCost)}</div>
                <div className="text-xs text-green-600 dark:text-green-400">{formatNumber(results.laborCost / results.totalCostPrice * 100, 1)}% от себестоимости</div>
              </div>
            )}
            
            {results.failureCost > 0 && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-100 dark:border-red-800">
                <div className="font-medium text-red-700 dark:text-red-300 mb-1">Брак</div>
                <div className="text-xl font-bold text-red-800 dark:text-red-200">{formatCurrencyWithSymbol(results.failureCost)}</div>
                <div className="text-xs text-red-600 dark:text-red-400">{formatNumber(results.failureCost / results.totalCostPrice * 100, 1)}% от себестоимости</div>
              </div>
            )}
            
            {results.overheadsCost > 0 && (
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-md border border-orange-100 dark:border-orange-800">
                <div className="font-medium text-orange-700 dark:text-orange-300 mb-1">Накладные</div>
                <div className="text-xl font-bold text-orange-800 dark:text-orange-200">{formatCurrencyWithSymbol(results.overheadsCost)}</div>
                <div className="text-xs text-orange-600 dark:text-orange-400">{formatNumber(results.overheadsCost / results.totalCostPrice * 100, 1)}% от себестоимости</div>
              </div>
            )}
            
            {results.profitAmount > 0 && (
              <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-md border border-emerald-100 dark:border-emerald-800">
                <div className="font-medium text-emerald-700 dark:text-emerald-300 mb-1">Прибыль</div>
                <div className="text-xl font-bold text-emerald-800 dark:text-emerald-200">{formatCurrencyWithSymbol(results.profitAmount)}</div>
                <div className="text-xs text-emerald-600 dark:text-emerald-400">{formatNumber(results.profitAmount / results.totalCostPrice * 100, 1)}% от себестоимости</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;